import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import type { SearchHit, SearchResponse } from "shared";
import { api } from "../api";
import { useCallerState } from "../callerState";
import { useLinkedCallerState } from "../linkedCallerState";
import { StatusBadge, VerificationBadge } from "../components/StatusBadge";
import { useProgramPanel } from "../programPanel";
import { Highlight } from "../search/Highlight";
import { WhyMatched } from "../search/WhyMatched";
import { STATES } from "../states";

export type SearchPageSearch = { q?: string; state?: string; category?: string; department?: string; jurisdiction?: string };
type Cat = { slug: string; name: string; children: { slug: string; name: string }[] };
type Dept = { id: string; slug: string; name: string; acronym: string | null; state_code: string | null };
const FILTER_KEYS = ["q", "state", "category", "department", "jurisdiction"] as const;
const titleLink = "font-medium text-brand-700 hover:underline dark:text-brand-100";

function HitText({ hit }: { hit: SearchHit }) {
  if (hit.snippet) return <p className="text-sm text-slate-600 dark:text-slate-400"><Highlight text={hit.snippet} /></p>;
  if (hit.summary) return <p className="line-clamp-2 text-sm text-slate-600 dark:text-slate-400">{hit.summary}</p>;
  return null;
}

function Group({ title, hits, children }: { title: string; hits: SearchHit[]; children: (h: SearchHit) => ReactNode }) {
  if (!hits.length) return null;
  return (
    <section aria-label={title} className="card">
      <h2 className="mb-1 text-sm font-semibold">{title} <span className="font-normal text-slate-500">({hits.length})</span></h2>
      <ul className="divide-y divide-slate-200 dark:divide-slate-800">
        {hits.map((h) => <li key={`${h.type}-${h.id}`} className="flex flex-col gap-0.5 py-2">{children(h)}</li>)}
      </ul>
    </section>
  );
}

function OwnerLink({ owner, state }: { owner: NonNullable<SearchHit["owner"]>; state?: string }) {
  const cls = "text-brand-700 hover:underline dark:text-brand-100";
  if (owner.type === "problem") return <Link to="/problems/$slug" params={{ slug: owner.slug }} search={{ state }} className={cls}>{owner.title}</Link>;
  if (owner.type === "program") return <Link to="/directory/programs/$slug" params={{ slug: owner.slug }} search={{ state }} className={cls}>{owner.title}</Link>;
  return <Link to="/directory/departments/$slug" params={{ slug: owner.slug }} className={cls}>{owner.title}</Link>;
}

function QueryNotes({ r }: { r: SearchResponse }) {
  const q = r.query;
  if (!q.corrections.length && !q.synonyms.length && !q.problems.length) return null;
  return (
    <div className="mb-3 flex flex-col gap-0.5 rounded-md bg-slate-100 px-3 py-2 text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-300">
      {q.corrections.length > 0 && (
        <div>Showing results for {q.corrections.map((c) => `“${c.to}”`).join(", ")} (you typed {q.corrections.map((c) => `“${c.from}”`).join(", ")}).</div>
      )}
      {q.synonyms.length > 0 && <div>Also searched: {q.synonyms.map((s) => `${s.to} (for “${s.from}”)`).join("; ")}</div>}
      {q.problems.length > 0 && <div>Recognised problem: {q.problems.map((p) => p.title).join(", ")}</div>}
    </div>
  );
}

export function SearchPage() {
  const search = useSearch({ from: "/app/search" });
  const navigate = useNavigate({ from: "/search" });
  const caller = useCallerState();
  const panel = useProgramPanel();
  // One caller's state for the call: a state in the address (the search box, Back, a shared link) becomes the
  // caller's state on arrival; after that the top bar and the State filter (which sets the caller's state) drive the
  // results, the panel and the address, which follows replaced. With no state in the address, the caller's is used.
  // "Any state" clears the caller's state as well, so nothing refills a filter the rep removed.
  const state = useLinkedCallerState(search.state,
    (s) => navigate({ search: (prev: SearchPageSearch) => ({ ...prev, state: s }), replace: true }));
  const set = (patch: Partial<SearchPageSearch>) => navigate({ search: (s: SearchPageSearch) => ({ ...s, ...patch }) });
  const [text, setText] = useState(search.q ?? "");
  useEffect(() => setText(search.q ?? ""), [search.q]);

  const filters: SearchPageSearch = { ...search, state };
  const qs = new URLSearchParams(FILTER_KEYS.flatMap((k) => (filters[k] ? [[k, String(filters[k])]] : []))).toString();
  const res = useQuery({
    queryKey: ["search", qs], enabled: !!search.q?.trim(), staleTime: 0, placeholderData: keepPreviousData,
    queryFn: () => api<SearchResponse>(`/api/search?${qs}`),
  });
  const cats = useQuery({ queryKey: ["categories"], queryFn: () => api<{ categories: Cat[] }>("/api/directory/categories") });
  const depts = useQuery({ queryKey: ["departments"], queryFn: () => api<{ departments: Dept[] }>("/api/directory/departments") });

  const submit = (e: FormEvent) => { e.preventDefault(); set({ q: text.trim() || undefined }); };
  const chooseState = (v: string) => caller.setState(v || undefined);
  const openProgram = (h: SearchHit) => panel.open(h.slug, h.stateCode ?? state);
  // keepPreviousData keeps the last results while new ones load: never after the search was cleared, and marked
  // "Updating…" (dimmed) while a changed query or filter loads, so the rep never reads them as the new results.
  const r = search.q?.trim() ? res.data : undefined;
  const updating = !!r && res.isPlaceholderData;
  const total = r ? r.groups.problems.length + r.groups.programs.length + r.groups.faqs.length + r.groups.departments.length : 0;

  return (
    <div className="mx-auto max-w-4xl">
      <form onSubmit={submit} className="mb-3 flex gap-2">
        <input className="input" aria-label="Refine search" value={text} onChange={(e) => setText(e.target.value)} placeholder="What is the caller dealing with?" />
        <button className="btn-primary">Search</button>
      </form>
      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {/* Sets the caller's state; named "State shown" so that only the top bar's control is "Caller's state". */}
        <div><label className="label" htmlFor="sr-state">State shown</label>
          <select id="sr-state" className="input" value={state ?? ""} onChange={(e) => chooseState(e.target.value)}>
            <option value="">Any state</option>
            {STATES.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
          </select></div>
        <div><label className="label" htmlFor="sr-category">Category</label>
          <select id="sr-category" className="input" value={search.category ?? ""} onChange={(e) => set({ category: e.target.value || undefined })}>
            <option value="">All categories</option>
            {cats.data?.categories.map((c) => (
              <optgroup key={c.slug} label={c.name}>
                <option value={c.slug}>All {c.name}</option>
                {c.children.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
              </optgroup>
            ))}
          </select></div>
        <div><label className="label" htmlFor="sr-department">Department</label>
          <select id="sr-department" className="input" value={search.department ?? ""} onChange={(e) => set({ department: e.target.value || undefined })}>
            <option value="">All departments</option>
            {depts.data?.departments.map((d) => (
              <option key={d.id} value={d.slug}>{d.name}{d.acronym ? ` (${d.acronym})` : ""}{d.state_code ? ` · ${d.state_code}` : ""}</option>
            ))}
          </select></div>
        <div><label className="label" htmlFor="sr-level">Level</label>
          <select id="sr-level" className="input" value={search.jurisdiction ?? ""} onChange={(e) => set({ jurisdiction: e.target.value || undefined })}>
            <option value="">All levels</option><option value="federal">Federal</option><option value="state">State</option><option value="local">Local</option>
          </select></div>
      </div>

      {!search.q && <p className="text-sm text-slate-500">Type what the caller describes, for example “lost job, behind on rent”.</p>}
      {res.isLoading && <p className="text-sm text-slate-500">Searching…</p>}
      {updating && <p role="status" className="mb-2 text-sm text-slate-500">Updating…</p>}
      {res.error && <p role="alert" className="text-sm text-red-600">Search failed: {(res.error as Error).message}</p>}
      {r && <div className={updating ? "opacity-50" : ""}><QueryNotes r={r} /></div>}
      {r && total === 0 && <p className={`text-sm text-slate-500 ${updating ? "opacity-50" : ""}`}>No results. Try other words or remove a filter.</p>}
      {r && (
        <div aria-busy={updating} className={`flex flex-col gap-4 transition-opacity ${updating ? "opacity-50" : ""}`}>
          <Group title="Problems" hits={r.groups.problems}>{(h) => (
            <>
              <Link to="/problems/$slug" params={{ slug: h.slug }} search={{ state }} className={titleLink}>{h.title}</Link>
              {h.subtitle && <div className="text-xs text-slate-500">{h.subtitle}</div>}
              <HitText hit={h} />
              <WhyMatched why={h.why} />
            </>
          )}</Group>
          <Group title="Programs" hits={r.groups.programs}>{(h) => (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" className={`text-left ${titleLink}`} onClick={() => openProgram(h)}>{h.title}</button>
                {h.status && <StatusBadge status={h.status} />}
                {h.verification && <VerificationBadge v={h.verification} />}
                {h.stateVerification && <span className="text-xs text-slate-500">{h.stateCode}: <VerificationBadge v={h.stateVerification} /></span>}
              </div>
              {h.subtitle && <div className="text-xs text-slate-500">{h.subtitle}</div>}
              <HitText hit={h} />
              <WhyMatched why={h.why} />
              <Link to="/directory/programs/$slug" params={{ slug: h.slug }} search={{ state: h.stateCode ?? state }}
                className="w-fit text-xs text-brand-700 hover:underline dark:text-brand-100">Open page</Link>
            </>
          )}</Group>
          <Group title="FAQs" hits={r.groups.faqs}>{(h) => (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{h.title}</span>
                {/* A stale, under-review or pending owner gets its badge (pending FAQs are not indexed). */}
                {h.verification && (h.verification.warning || h.verification.status === "pending") && <VerificationBadge v={h.verification} />}
              </div>
              <HitText hit={h} />
              {h.owner && <div className="text-xs text-slate-500">From: <OwnerLink owner={h.owner} state={h.stateCode ?? state} /></div>}
              <WhyMatched why={h.why} />
            </>
          )}</Group>
          <Group title="Departments" hits={r.groups.departments}>{(h) => (
            <>
              <Link to="/directory/departments/$slug" params={{ slug: h.slug }} className={titleLink}>{h.title}</Link>
              {h.subtitle && <div className="text-xs text-slate-500">{h.subtitle}</div>}
              <HitText hit={h} />
              <WhyMatched why={h.why} />
            </>
          )}</Group>
        </div>
      )}
    </div>
  );
}
